import { useEffect, useRef } from 'react'
import { useDataSourceStore } from '@/stores/dataSourceStore'

/**
 * Hook to get datasource data from the centralized store
 * This replaces direct usage of useDataSourceQuery to prevent duplicate API calls
 */
export function useDataSourceFromStore(id: string) {
  const {
    dataSources,
    loading,
    errors,
    fetchDataSource,
    backgroundRefresh
  } = useDataSourceStore()

  // Track the previous id to detect changes
  const prevIdRef = useRef<string>(id)
  // Track if we've triggered a background refresh for this id
  const hasRefreshedRef = useRef<string>('')

  // Get current state directly from store - no local state to get stale
  const cachedData = id ? dataSources.get(id) : undefined
  const loadingState = id ? (loading.get(id) || false) : false
  const errorState = id ? (errors.get(id) || null) : null

  // Determine if we're loading (either explicitly loading or fetching new data)
  const isLoading = loadingState || (id !== '' && !cachedData && !errorState)

  useEffect(() => {
    // Reset tracking when id changes
    if (prevIdRef.current !== id) {
      prevIdRef.current = id
      hasRefreshedRef.current = ''
    }

    if (!id) {
      return
    }

    // Fetch if not cached and not loading and there is no prior error
    // This prevents infinite re-fetch loops when the API returns errors (e.g., 500)
    if (!cachedData && !loadingState && !errorState) {
      fetchDataSource(id).catch(() => {
        // Error is handled in the store
      })
    } else if (cachedData && hasRefreshedRef.current !== id) {
      // Data is cached - trigger a silent background refresh to get latest data
      hasRefreshedRef.current = id
      backgroundRefresh(id)
    }
  }, [id, cachedData, loadingState, errorState, fetchDataSource, backgroundRefresh])

  return {
    data: cachedData,
    isLoading,
    error: errorState,
    // Ensure the function is explicitly async to satisfy lint rule
    refetch: async () => fetchDataSource(id)
  }
}